"use client";

import { c as _c } from "react/compiler-runtime";
import type { FacetsCounts, FilterFacet_Legacy, FilterInfo, FilterResultPage } from "../types/filter";
import { useFilterOptions } from "./use-filter-options";
import { useFilterSearch } from "./use-filter-search";
import { useHybridSearch } from "./use-hybrid-search";
import { useSearchQueryContext } from "./use-skill-vector-context";

/**
 * Combines static filter information with dynamic search results.
 * When a search question is active, uses hybrid search (Typesense constrained by skill matcher IDs)
 * to ensure facet counts reflect the semantic search subset.
 * Otherwise, uses plain Typesense filter search.
 */
export const useInfo = () => {
  const $ = _c(6);
  const filterInfo = useFilterOptions();
  const {
    searchQuestion,
    rerankInstruction
  } = useSearchQueryContext();
  const filterResults = useFilterSearch();
  let t0;
  if ($[0] !== rerankInstruction || $[1] !== searchQuestion) {
    t0 = searchQuestion ? {
      searchQuestion,
      rerankInstruction: rerankInstruction ?? ""
    } : undefined;
    $[0] = rerankInstruction;
    $[1] = searchQuestion;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const hybridResults = useHybridSearch(t0);
  const resultsPage = searchQuestion ? hybridResults.data?.pages?.[0] : filterResults.data?.pages?.[0];
  let t1;
  if ($[3] !== filterInfo || $[4] !== resultsPage) {
    t1 = mergeFilterInfo(filterInfo, resultsPage);
    $[3] = filterInfo;
    $[4] = resultsPage;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const mergedFilterInfo = t1;
  return mergedFilterInfo;
};

/**
 * Merges static filter information with dynamic results from the search
 * @param filterInfo - Static filter information containing base structure
 * @param resultsPage - Dynamic results page containing updated counts
 */
function mergeFilterInfo(filterInfo: FilterInfo, resultsPage?: FilterResultPage): FilterInfo {
  return {
    ...filterInfo,
    facetsCounts: mergeFacetCounts(filterInfo.facetsCounts, resultsPage?.facetsCounts),
    branches: updateBranchCounts(filterInfo.branches, resultsPage),
    locationTree: updateLocationTreeCounts(filterInfo.locationTree, resultsPage)
  };
}

/**
 * Combines static facet counts with dynamic counts from search results
 * @param staticCounts - Base facet counts from static configuration
 * @param dynamicCounts - Updated facet counts from search results
 */
function mergeFacetCounts(staticCounts: FacetsCounts, dynamicCounts?: FacetsCounts) {
  return Object.fromEntries(Object.entries(staticCounts).map(([key, staticValues]) => {
    const dynamicValues = dynamicCounts?.[key as FilterFacet_Legacy] || {};
    return [key, mergeFacetValues(staticValues, dynamicValues)];
  }));
}

/**
 * Merges static facet values with dynamic values, ensuring all static values exist
 * @param staticValues - Base values from static configuration
 * @param dynamicValues - Updated values from search results
 */
function mergeFacetValues(staticValues: Record<string, number>, dynamicValues: Record<string, number>) {
  const merged = {
    ...dynamicValues
  };
  for (const key of Object.keys(staticValues)) {
    const trimmed = key.trim();
    merged[trimmed] ??= 0;
  }
  return merged;
}

/**
 * Updates branch information with job counts from search results
 * @param branches - Array of branch information
 * @param resultsPage - Search results containing updated counts
 */
function updateBranchCounts(branches: FilterInfo["branches"], resultsPage?: FilterResultPage) {
  return branches.map(branch => ({
    ...branch,
    jobCount: resultsPage?.facetsCounts?.["data.locations.name"]?.[branch.name] || 0
  }));
}

/**
 * Updates the location tree with current counts from search results
 * Handles the hierarchical structure of country -> state -> city
 * @param locationTree - Hierarchical location structure
 * @param resultsPage - Search results containing updated counts
 */
function updateLocationTreeCounts(locationTree: FilterInfo["locationTree"], resultsPage?: FilterResultPage) {
  return locationTree.map(country => ({
    ...country,
    count: getLocationCount(country.value, resultsPage),
    children: country.children?.map(state => ({
      ...state,
      count: getLocationCount(state.value, resultsPage),
      children: state.children?.map(city => ({
        ...city,
        count: getLocationCount(city.value, resultsPage)
      }))
    }))
  }));
}

/**
 * Retrieves the count for a specific location from search results
 * @param value - Location value in format "facet:name"
 * @param resultsPage - Search results containing counts
 */
function getLocationCount(value: string, resultsPage?: FilterResultPage) {
  const [facet, name] = value.split(":");
  return resultsPage?.facetsCounts?.[facet as FilterFacet_Legacy]?.[name] || 0;
}